import { useMemo } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import { Select, SortDir, TextInput } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Divider } from '@mui/material';

const TOP_SORT_DIR_OPTIONS = [
  { title: 'Top N', value: SortDir.DESC },
  { title: 'Bottom N', value: SortDir.ASC },
];

interface FilterByAdvancedProps {
  advancedFilter: { dir?: SortDir; top?: number; value?: string; type?: string };
  onChange: (value: { dir?: SortDir; top?: number; value?: string; type?: string }) => void;
  isHistorical: boolean | undefined;
  activeProjName?: string;
}

function FilterByAdvanced({ advancedFilter, onChange, isHistorical, activeProjName }: FilterByAdvancedProps) {
  const {
    measurements,
    accountEntities: { aggProjs },
  } = useAvContext();

  const measurementsOptions = useMemo(
    () =>
      measurements.visible
        .filter(metric => (isHistorical ? metric.timeExpression : metric.expression))
        .filter(metric =>
          activeProjName && aggProjs[activeProjName]?.entityTypeId?.name
            ? metric.context.includes(aggProjs[activeProjName].entityTypeId.name)
            : true
        )
        .map(({ displayName, systemName }) => ({ title: displayName, value: systemName })),
    [measurements, isHistorical, activeProjName, aggProjs]
  );

  return (
    <Box sx={{ ...flex.col, gap: '20px', padding: '16px 16px 24px 16px' }}>
      <Select
        value={advancedFilter.dir}
        options={TOP_SORT_DIR_OPTIONS}
        label="Type"
        size="small"
        style={{ maxWidth: 'unset', '.MuiFormControl-root': { width: '100%', maxWidth: 'unset' } }}
        onChange={dir => onChange({ ...advancedFilter, type: 'TOP_K', dir })}
      />

      <Divider />

      <Box sx={{ ...flex.row, gap: '12px' }}>
        <TextInput
          label={advancedFilter.dir === SortDir.DESC ? 'Top' : 'Bottom'}
          type="number"
          size="small"
          sx={{ width: '108px' }}
          value={advancedFilter.top}
          onChange={(top: number) => onChange({ ...advancedFilter, type: 'TOP_K', top })}
          disableClearButton
        />

        <Select
          value={advancedFilter.value}
          options={measurementsOptions}
          label="By Value"
          size="small"
          onChange={value => onChange({ ...advancedFilter, type: 'TOP_K', value })}
        />
      </Box>
    </Box>
  );
}

export default FilterByAdvanced;
